import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Dialog from "../../../components/ui/Dialog";
import BranchSelect from "../../../components/ui/BranchSelect";
import PickerChips from "../../../components/ui/PickerChips";
import {
  useCreateSeminar,
  useSkills,
  useUpdateSeminar,
} from "../../../api/queries/learningQueries";
import { getApiErrorMessage } from "../../../utils/apiError";
import { useCompanies } from "../../../api/queries/organizationQueries";
import useCurrentUser from "../../../hooks/useCurrentUser";
import {
  SEMINAR_MODES,
  SEMINAR_TYPES,
  fromLocalInput,
  titleCase,
  toLocalInput,
} from "./learningFormat";

const ACTIVE_SKILLS = { includeInactive: false };

const initial = (s, defaultCompanyId) => ({
  companyId: s?.companyId || s?.company?.id || defaultCompanyId || "",
  title: s?.title || "",
  description: s?.description || "",
  type: s?.type || "SEMINAR",
  mode: s?.mode || "ONSITE",
  venue: s?.venue || "",
  meetingLink: s?.meetingLink || "",
  facilitator: s?.facilitator || "",
  startAt: toLocalInput(s?.startAt),
  endAt: toLocalInput(s?.endAt),
  hours: s?.hours ?? "",
  capacity: s?.capacity ?? "",
  branchId: s?.branchId || s?.branch?.id || "",
  openEnrollment: !!s?.openEnrollment,
  skillIds: (s?.skills || []).map((k) => k.id),
});

/** Create or edit a seminar. Times are entered in Manila time. */
const SeminarFormDialog = ({ seminar, onClose }) => {
  const navigate = useNavigate();
  const isEdit = !!seminar;
  // Company: the working/selected company (super admin), else the user's own;
  // HR of a parent company can pick a subsidiary. Sent on create.
  const user = useCurrentUser();
  const { data: companies = [] } = useCompanies();
  const [form, setForm] = useState(() => initial(seminar, user.companyId));
  const [errors, setErrors] = useState([]);
  const create = useCreateSeminar();
  const update = useUpdateSeminar();
  const busy = create.isPending || update.isPending;
  const skills = useSkills(ACTIVE_SKILLS);
  const skillOptions = useMemo(
    () => (skills.data || []).map((k) => ({ value: k.id, label: k.name })),
    [skills.data],
  );
  const set = (changes) => setForm((f) => ({ ...f, ...changes }));
  const online = form.mode !== "ONSITE";
  const onsite = form.mode !== "ONLINE";

  const submit = (e) => {
    e.preventDefault();
    const list = [];
    if (!form.title.trim()) list.push("Enter the title.");
    if (!isEdit && !form.companyId) list.push("Choose the company.");
    if (!form.startAt || !form.endAt) list.push("Enter the start and end.");
    else if (form.endAt <= form.startAt)
      list.push("The end must be after the start.");
    if (form.capacity !== "" && Number(form.capacity) < 1)
      list.push("Capacity must be at least 1.");
    setErrors(list);
    if (list.length) return;

    const body = {
      title: form.title.trim(),
      description: form.description.trim() || undefined,
      type: form.type,
      mode: form.mode,
      venue: onsite ? form.venue.trim() || undefined : undefined,
      meetingLink: online ? form.meetingLink.trim() || undefined : undefined,
      facilitator: form.facilitator.trim() || undefined,
      startAt: fromLocalInput(form.startAt),
      endAt: fromLocalInput(form.endAt),
      hours: form.hours === "" ? undefined : Number(form.hours),
      capacity: form.capacity === "" ? undefined : Number(form.capacity),
      branchId: form.branchId || undefined,
      ...(!isEdit && { companyId: form.companyId }),
      openEnrollment: form.openEnrollment,
      skillIds: form.skillIds,
    };
    const onError = (err) => {
      const msg = err?.response?.data?.message;
      setErrors(
        Array.isArray(msg)
          ? msg
          : [getApiErrorMessage(err, "Could not save the seminar.")],
      );
    };
    if (isEdit) {
      update.mutate(
        { id: seminar.id, ...body },
        {
          onSuccess: () => {
            toast.success("Seminar updated.");
            onClose();
          },
          onError,
        },
      );
    } else {
      create.mutate(body, {
        onSuccess: (s) => {
          toast.success("Seminar created. Now assign participants.");
          onClose();
          if (s?.id) navigate(`/learning/seminars/${s.id}`);
        },
        onError,
      });
    }
  };

  return (
    <Dialog
      title={isEdit ? "Edit seminar" : "New seminar"}
      size="lg"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="seminar-form"
            className="btn btn-primary profile-btn"
            disabled={busy}
          >
            {busy && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            {isEdit ? "Save" : "Create"}
          </button>
        </>
      }
    >
      <form id="seminar-form" onSubmit={submit} noValidate>
        {errors.length > 0 && (
          <div className="alert alert-danger fs-085 py-2">
            {errors.length === 1 ? (
              errors[0]
            ) : (
              <ul className="mb-0 ps-3">
                {errors.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        <div className="row g-3">
          <div className="col-12">
            <label htmlFor="sem-title" className="profile-label">
              Title <span className="text-danger">*</span>
            </label>
            <input
              id="sem-title"
              className="form-control"
              value={form.title}
              onChange={(e) => set({ title: e.target.value })}
              autoFocus
            />
          </div>
          <div className="col-sm-6">
            <label htmlFor="sem-type" className="profile-label">
              Type
            </label>
            <select
              id="sem-type"
              className="form-select"
              value={form.type}
              onChange={(e) => set({ type: e.target.value })}
            >
              {SEMINAR_TYPES.map((t) => (
                <option key={t} value={t}>
                  {titleCase(t)}
                </option>
              ))}
            </select>
          </div>
          <div className="col-sm-6">
            <label htmlFor="sem-mode" className="profile-label">
              Mode
            </label>
            <select
              id="sem-mode"
              className="form-select"
              value={form.mode}
              onChange={(e) => set({ mode: e.target.value })}
            >
              {SEMINAR_MODES.map((m) => (
                <option key={m} value={m}>
                  {titleCase(m)}
                </option>
              ))}
            </select>
          </div>
          <div className="col-sm-6">
            <label htmlFor="sem-start" className="profile-label">
              Start <span className="text-danger">*</span>
            </label>
            <input
              id="sem-start"
              type="datetime-local"
              className="form-control"
              value={form.startAt}
              onChange={(e) => set({ startAt: e.target.value })}
            />
          </div>
          <div className="col-sm-6">
            <label htmlFor="sem-end" className="profile-label">
              End <span className="text-danger">*</span>
            </label>
            <input
              id="sem-end"
              type="datetime-local"
              className="form-control"
              value={form.endAt}
              min={form.startAt || undefined}
              onChange={(e) => set({ endAt: e.target.value })}
            />
          </div>
          {onsite && (
            <div className={online ? "col-sm-6" : "col-12"}>
              <label htmlFor="sem-venue" className="profile-label">
                Venue
              </label>
              <input
                id="sem-venue"
                className="form-control"
                value={form.venue}
                onChange={(e) => set({ venue: e.target.value })}
                placeholder="e.g. Makati HQ, 5F Training Room"
              />
            </div>
          )}
          {online && (
            <div className={onsite ? "col-sm-6" : "col-12"}>
              <label htmlFor="sem-link" className="profile-label">
                Meeting link
              </label>
              <input
                id="sem-link"
                type="url"
                className="form-control"
                value={form.meetingLink}
                onChange={(e) => set({ meetingLink: e.target.value })}
                placeholder="https://…"
              />
            </div>
          )}
          <div className="col-sm-6">
            <label htmlFor="sem-facilitator" className="profile-label">
              Facilitator
            </label>
            <input
              id="sem-facilitator"
              className="form-control"
              value={form.facilitator}
              onChange={(e) => set({ facilitator: e.target.value })}
            />
          </div>
          <div className="col-sm-3">
            <label htmlFor="sem-hours" className="profile-label">
              Hours
            </label>
            <input
              id="sem-hours"
              type="number"
              min="0"
              step="0.5"
              className="form-control"
              value={form.hours}
              onChange={(e) => set({ hours: e.target.value })}
              placeholder="auto"
            />
          </div>
          <div className="col-sm-3">
            <label htmlFor="sem-capacity" className="profile-label">
              Capacity
            </label>
            <input
              id="sem-capacity"
              type="number"
              min="1"
              className="form-control"
              value={form.capacity}
              onChange={(e) => set({ capacity: e.target.value })}
              placeholder="no limit"
            />
          </div>
          <div className="col-sm-6">
            <label htmlFor="sem-company" className="profile-label">
              Company {!isEdit && <span className="text-danger">*</span>}
            </label>
            <select
              id="sem-company"
              className="form-select"
              value={form.companyId}
              onChange={(e) => set({ companyId: e.target.value, branchId: "" })}
              disabled={isEdit || companies.length <= 1}
              required={!isEdit}
            >
              <option value="">Choose a company…</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.companyName || c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-sm-6">
            <label htmlFor="sem-branch" className="profile-label">
              Branch
            </label>
            <BranchSelect
              id="sem-branch"
              companyId={form.companyId}
              value={form.branchId}
              onChange={(v) => set({ branchId: v || "" })}
              emptyLabel="All branches"
            />
          </div>
          <div className="col-sm-6 d-flex align-items-end">
            <div className="form-check form-switch">
              <input
                id="sem-open"
                type="checkbox"
                className="form-check-input"
                checked={form.openEnrollment}
                onChange={(e) => set({ openEnrollment: e.target.checked })}
              />
              <label htmlFor="sem-open" className="form-check-label fs-085">
                Open enrollment (employees can sign up in the portal)
              </label>
            </div>
          </div>
          <div className="col-12">
            <label htmlFor="sem-skills" className="profile-label">
              Skills it builds
            </label>
            <PickerChips
              id="sem-skills"
              options={skillOptions}
              value={form.skillIds}
              onChange={(skillIds) => set({ skillIds })}
              placeholder="Add skill…"
              emptyText="No skills linked"
            />
          </div>
          <div className="col-12">
            <label htmlFor="sem-desc" className="profile-label">
              Description
            </label>
            <textarea
              id="sem-desc"
              className="form-control"
              rows={3}
              value={form.description}
              onChange={(e) => set({ description: e.target.value })}
            />
          </div>
        </div>
      </form>
    </Dialog>
  );
};

export default SeminarFormDialog;
